import React, { useState, useEffect, useRef } from 'react';
import { User, ChevronLeft, AlertCircle, ChevronDown, Clock, CheckCircle } from 'lucide-react';

export default function CreateOrderPage({ base, material: initialMaterial, reorderData, initialMode, onBack, onSubmit, onReadContract, serviceContractAgreed }) {
  const [mode, setMode] = useState(initialMode || 'self');
  const [selectedMaterial, setSelectedMaterial] = useState(initialMaterial?.name || '重型废钢 (HMS1)');
  const [weight, setWeight] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [hasDraft, setHasDraft] = useState(false);
  const [supplierHistory, setSupplierHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [nameError, setNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [weightError, setWeightError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const draftLoadedRef = useRef(false);

  const isOtherMode = mode === 'other';
  
  const isClosedDemand = initialMaterial?.demandType === 'closed';
  const fixedWeight = initialMaterial?.fixedWeight;
  const deliveryDeadline = initialMaterial?.deliveryDeadline;

  useEffect(() => {
    if (serviceContractAgreed) {
      setAgreed(true);
    }
  }, [serviceContractAgreed]);

  useEffect(() => {
    const saved = localStorage.getItem('supplierHistory');
    if (saved) {
      setSupplierHistory(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    if (reorderData) {
      setMode(reorderData.mode || 'self');
      setSelectedMaterial(reorderData.material?.name || '重型废钢 (HMS1)');
      setWeight(reorderData.weight || '');
      setSupplierName(reorderData.supplierInfo?.name || '');
      setSupplierPhone(reorderData.supplierInfo?.phone || '');
    } else if (isClosedDemand && fixedWeight) {
      setWeight(fixedWeight.toString());
    } else if (!isClosedDemand) {
      // 开口需求默认值 2000 吨
      setWeight('2000');
    }
  }, [reorderData, isClosedDemand, fixedWeight]);

  useEffect(() => {
    if (reorderData) return;
    if (draftLoadedRef.current) return;
    
    const draft = localStorage.getItem('orderDraft');
    if (draft) {
      const draftData = JSON.parse(draft);
      if (draftData.baseId === base?.id) {
        if (initialMode) {
          setMode(initialMode);
        } else {
          setMode(draftData.mode || 'self');
        }
        setSelectedMaterial(draftData.selectedMaterial || initialMaterial?.name || '重型废钢 (HMS1)');
        if (draftData.weight) {
          setWeight(draftData.weight);
        }
        setSupplierName(draftData.supplierName || '');
        setSupplierPhone(draftData.supplierPhone || '');
        if (!initialMode) {
          setHasDraft(true);
        }
      }
    }
    draftLoadedRef.current = true;
  }, [reorderData, initialMaterial, base?.id, initialMode]);

  useEffect(() => {
    if (reorderData) return;
    const draftData = {
      mode,
      selectedMaterial,
      weight,
      supplierName,
      supplierPhone,
      baseId: base?.id,
      timestamp: Date.now()
    };
    localStorage.setItem('orderDraft', JSON.stringify(draftData));
  }, [mode, selectedMaterial, weight, supplierName, supplierPhone, base, reorderData]);

  const clearDraft = () => {
    localStorage.removeItem('orderDraft');
    setHasDraft(false);
  };

  const validateSupplierInfo = () => {
    let valid = true;
    if (!supplierName.trim()) {
      setNameError('请输入实供人姓名');
      valid = false;
    } else {
      setNameError('');
    }
    if (!supplierPhone.trim()) {
      setPhoneError('请输入实供人手机号');
      valid = false;
    } else if (!/^1[3-9]\d{9}$/.test(supplierPhone.trim())) {
      setPhoneError('请输入正确的手机号');
      valid = false;
    } else {
      setPhoneError('');
    }
    return valid;
  };

  const validateWeight = () => {
    if (!weight) {
      setWeightError('请输入预估送货重量');
      return false;
    }
    const weightNum = parseInt(weight, 10);
    if (isNaN(weightNum) || weightNum <= 0) {
      setWeightError('预估送货重量必须是正整数');
      return false;
    }
    if (weightNum.toString() !== weight.trim()) {
      setWeightError('预估送货重量必须是正整数');
      return false;
    }
    setWeightError('');
    return true;
  };

  const saveSupplierToHistory = () => {
    if (!supplierName.trim() || !supplierPhone.trim()) return;
    const newEntry = { name: supplierName.trim(), phone: supplierPhone.trim(), timestamp: Date.now() };
    const existing = supplierHistory.find(h => h.name === newEntry.name && h.phone === newEntry.phone);
    if (existing) {
      const updated = supplierHistory.map(h => 
        h.name === newEntry.name && h.phone === newEntry.phone 
          ? { ...h, timestamp: Date.now() }
          : h
      );
      setSupplierHistory(updated);
      localStorage.setItem('supplierHistory', JSON.stringify(updated));
    } else {
      const updated = [newEntry, ...supplierHistory].slice(0, 10);
      setSupplierHistory(updated);
      localStorage.setItem('supplierHistory', JSON.stringify(updated));
    }
  };

  const handleSelectHistory = (item) => {
    setSupplierName(item.name);
    setSupplierPhone(item.phone);
    setNameError('');
    setPhoneError('');
    setShowHistory(false);
  };

  const handleSubmit = () => {
    const finalWeight = isClosedDemand ? fixedWeight : parseInt(weight.trim(), 10);
    if (!isClosedDemand && !validateWeight()) {
      return;
    }
    if (isOtherMode && !validateSupplierInfo()) {
      return;
    }
    if (isOtherMode) {
      saveSupplierToHistory();
    }
    clearDraft();
    const orderData = {
      base,
      material: selectedMaterial,
      weight: finalWeight,
      mode,
      supplierInfo: mode === 'other' ? { name: supplierName.trim(), phone: supplierPhone.trim() } : null,
      deliveryDeadline: isClosedDemand ? deliveryDeadline : null,
      demandType: isClosedDemand ? 'closed' : 'open'
    };
    onSubmit(orderData);
  };

  const canSubmit = () => {
    if (!weight) return false;
    const weightNum = parseInt(weight, 10);
    if (isNaN(weightNum) || weightNum <= 0) return false;
    if (weightNum.toString() !== weight.trim()) return false;
    if (isOtherMode) {
      if (!supplierName.trim() || !supplierPhone.trim()) return false;
      if (!/^1[3-9]\d{9}$/.test(supplierPhone.trim())) return false;
      if (!agreed) return false;
    }
    return true;
  };

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="bg-white sticky top-0 z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button
          onClick={onBack}
          className="p-1 -ml-2"
        >
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <h2 className="font-bold ml-2 text-slate-800 text-lg">
          {mode === 'self' ? '自己卖' : '帮别人卖'}
        </h2>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle size={16} />
            <span className="text-sm font-medium">收货基地</span>
          </div>
          <div className="font-bold text-xl">{base?.name || '马钢诚兴马鞍山基地'}</div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 space-y-4">
          {initialMaterial ? (
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">已选物料</label>
              <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 font-medium">
                {selectedMaterial}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">选择物料</label>
              <select 
                value={selectedMaterial}
                onChange={(e) => setSelectedMaterial(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              >
                <option>重型废钢 (HMS1)</option>
                <option>中型废钢 (HMS2)</option>
                <option>小型加工废钢 (Q2)</option>
                <option>一级破碎料</option>
              </select>
            </div>
          )}

          {mode === 'other' && (
            <div className="p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border border-orange-200 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-orange-800 text-sm font-bold">
                  <User size={16} />
                  实供人信息
                  <span className="text-red-500">*</span>
                </div>
                {supplierHistory.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowHistory(!showHistory)}
                    className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    <Clock size={12} />
                    历史记录
                    <ChevronDown size={12} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>

              {showHistory && supplierHistory.length > 0 && (
                <div className="bg-white rounded-lg border border-orange-200 overflow-hidden mb-2">
                  {supplierHistory.map((item, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectHistory(item)}
                      className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-orange-50 transition-colors border-b border-orange-100 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <User size={14} className="text-orange-600" />
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-medium text-slate-800">{item.name}</div>
                          <div className="text-xs text-slate-500">{item.phone}</div>
                        </div>
                      </div>
                      <ChevronDown size={16} className="text-slate-400 -rotate-90" />
                    </button>
                  ))}
                </div>
              )}

              <div>
                <input
                  type="text"
                  value={supplierName}
                  onChange={(e) => {
                    setSupplierName(e.target.value);
                    if (e.target.value.trim()) setNameError('');
                  }}
                  placeholder="实供人姓名"
                  className={`w-full bg-white border rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all ${
                    nameError ? 'border-red-400' : 'border-orange-200'
                  }`}
                />
                {nameError && (
                  <p className="mt-1 text-xs text-red-500">{nameError}</p>
                )}
              </div>
              <div>
                <input
                  type="tel"
                  value={supplierPhone}
                  onChange={(e) => {
                    setSupplierPhone(e.target.value);
                    if (e.target.value.trim()) setPhoneError('');
                  }}
                  placeholder="实供人手机号"
                  className={`w-full bg-white border rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all ${
                    phoneError ? 'border-red-400' : 'border-orange-200'
                  }`}
                />
                {phoneError && (
                  <p className="mt-1 text-xs text-red-500">{phoneError}</p>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                <div className="flex items-start gap-2">
                  <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700 leading-relaxed">
                    <span className="font-bold">重要提示：</span>
                    实供人连续12个月"反向开票"累计销售额不得超过500万元。超过限额后需办理经营主体登记，自行开具发票。请务必告知实供人相关政策。
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              预估送货重量
              <span className="text-red-500 ml-1">*</span>
            </label>
            {isClosedDemand ? (
              <div className="relative">
                <div className="w-full bg-purple-50 border border-purple-200 rounded-lg p-3 pr-12 text-sm text-purple-800 font-medium flex items-center gap-2">
                  <Clock size={14} className="text-purple-500" />
                  <span>{fixedWeight} 吨（固定）</span>
                </div>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 text-sm">吨</span>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={weight}
                  onChange={(e) => {
                    setWeight(e.target.value);
                    if (e.target.value.trim()) setWeightError('');
                  }}
                  placeholder="请输入重量（正整数）"
                  className={`w-full bg-slate-50 border rounded-lg p-3 pr-12 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all ${
                    weightError ? 'border-red-400' : 'border-slate-200'
                  }`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">吨</span>
              </div>
            )}
            {weightError && (
              <p className="mt-1 text-xs text-red-500">{weightError}</p>
            )}
            {isClosedDemand && (
              <p className="mt-1 text-xs text-purple-500">闭口需求送货量固定，不可修改</p>
            )}
          </div>

          {!isOtherMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-700 leading-relaxed">
                  <span className="font-bold">重要提示：</span>
                  实供人连续12个月"反向开票"累计销售额不得超过500万元。超过限额后需办理经营主体登记，自行开具发票。
                </div>
              </div>
            </div>
          )}
        </div>

        {isOtherMode && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-start gap-3">
              <div
                onClick={() => {
                  if (!agreed && serviceContractAgreed) {
                    setAgreed(true);
                  } else if (agreed) {
                    setAgreed(false);
                  } else if (!serviceContractAgreed) {
                    onReadContract && onReadContract();
                  }
                }}
                className={`mt-0.5 w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                  agreed
                    ? 'bg-blue-500 border-blue-500'
                    : serviceContractAgreed
                    ? 'border-slate-300 bg-white hover:border-blue-400'
                    : 'border-slate-300 bg-white hover:border-blue-400'
                }`}
              >
                {agreed && <CheckCircle size={14} className="text-white" />}
              </div>
              <div className="flex-1">
                <div className="text-sm text-slate-600 leading-relaxed">
                  {serviceContractAgreed ? (
                    <>
                      我已阅读并同意 
                      <span className="font-medium text-green-600">
                        《平台服务协议》✓
                      </span>
                    </>
                  ) : (
                    <>
                      我已阅读并同意 
                      <span className="font-medium text-blue-500 cursor-pointer" onClick={() => onReadContract && onReadContract()}>
                        《平台服务协议》
                      </span>
                    </>
                  )}
                </div>
                <p className="mt-1 text-slate-400 text-xs">勾选即代表签署电子合同，系统将自动生成具有法律效力的电子文本。</p>
                {!serviceContractAgreed && !agreed && (
                  <div className="mt-2 flex items-center gap-2 text-orange-600 text-xs font-medium">
                    <AlertCircle size={12} />
                    <span>请先阅读并同意平台服务协议</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white border-t border-slate-200 p-4 pointer-events-auto">
          {hasDraft && (
            <div className="flex items-center justify-center gap-2 mb-2 text-xs text-blue-600 bg-blue-50 py-1.5 rounded-lg">
              <AlertCircle size={12} />
              <span>检测到未完成的订单草稿，已自动恢复</span>
            </div>
          )}
          <button
            disabled={!canSubmit()}
            onClick={handleSubmit}
            className={`w-full py-3.5 rounded-xl font-bold text-white shadow-lg transition-all ${
              canSubmit()
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl active:scale-[0.98]'
                : 'bg-slate-300 cursor-not-allowed'
            }`}
          >
            {isOtherMode ? '提交订单' : '下一步'}
          </button>
        </div>
      </div>
    </div>
  );
}
